import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  Add,
  AutoAwesomeRounded,
  ChevronRightSharp,
  ClearRounded,
  InfoOutlined,
  SaveOutlined,
  Verified,
  VisibilityOffRounded,
  VisibilityRounded,
} from '@mui/icons-material';
import { AccordionDetails, Badge, Box, TextField, Tooltip, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useAutoScrollHighlight } from '../../../../hooks/useAutoScrollHighlight';
import { useFetchData } from '../../../../hooks/useFetchData';
import { QuestionLibraryTabs } from '../../../../pages/DealGPT/QuestionLibrary';
import {
  createCategory,
  deleteCollection,
  reorderCategories,
  updateCollection,
} from '../../../../services/dealGPT';
import {
  CustomTooltip,
  StyledAccordion,
  StyledAccordionSummary,
  StyledIconButton,
} from '../../../../theme/CustomComponents';
import { CollectionWithCreator, QuestionCategory } from '../../../../Types/dealGPT';
import { CollectionTags } from '../../../../Types/enums';
import { collectionCategoriesPathUrl } from '../../../../util/routesUrls';
import { AnalyticsEvent, useAnalytics } from '../../../Providers/AnalyticsProvider';
import { useAuth } from '../../../Providers/AuthProvider';
import { useFeatures } from '../../../Providers/FeatureProvider';
import ShareButton from '../../../ShareButton';
import ConfirmationDialog from '../ConfirmationDialog';
import EditableDialog from '../EditableDialog';
import TagSummaryChips from '../TagSummaryChips';
import CategoryAccordion from './CategoryAccordion';
import CategoryAccordionSkeleton from './CategoryAccordionSkeleton';
import CollectionContextMenu from './CollectionContextMenu';
import CollectionForm from './CollectionForm';
import QuestionLibraryDefaultPrompt from './DefaultPrompt';
import { useReorderableList } from './useReorderableList';

interface CollectionAccordionProps {
  tab: QuestionLibraryTabs;
  collection: CollectionWithCreator;
  defaultExpanded?: boolean;
  autoScroll?: boolean;
  onScrolled?: () => void;
  refetchCollections: () => void;
}

const CollectionAccordion: React.FC<CollectionAccordionProps> = ({
  tab,
  collection,
  defaultExpanded,
  autoScroll,
  onScrolled,
  refetchCollections,
}) => {
  const { currentUser, isAdmin } = useAuth();
  const analytics = useAnalytics();
  const features = useFeatures();
  const [showPublicConfirmationDialog, setShowPublicConfirmationDialog] = useState(false);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
  const [showEditPromptDialog, setShowEditPromptDialog] = useState(false);
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded ?? false);
  const [reorderingCategories, setReorderingCategories] = useState<boolean>(false);
  const [editingCollection, setEditingCollection] = useState<boolean>(false);
  const [newCategory, setNewCategory] = useState<{ name: string; category?: string } | undefined>(
    undefined
  );
  const { highlight, ref, highlightSx } = useAutoScrollHighlight({ autoScroll, onScrolled });
  const sensors = useSensors(useSensor(PointerSensor));

  const {
    data: categories,
    loading: loadingCategories,
    refetch: refetchCategories,
  } = useFetchData<QuestionCategory[]>({
    urlPath: collectionCategoriesPathUrl(collection.id),
    skip: !expanded,
  });

  const {
    orderedItems: orderedCategories,
    setOrderedItems: setOrderedCategories,
    handleDragEnd,
  } = useReorderableList<QuestionCategory>(categories ?? []);

  const handleAccordionChange = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const onNewCategory = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setNewCategory({ name: '' });
      if (expanded) e.stopPropagation();
    },
    [expanded]
  );

  const handleSaveCategory = useCallback(async () => {
    if (!newCategory?.name) return;

    try {
      analytics.event(
        AnalyticsEvent.WM_IA_QL_ADD_CATEGORY,
        {
          value: `Category=${newCategory.name}`,
        },
        {
          categoryName: newCategory.name,
        }
      );

      await createCategory({
        collectionId: collection.id,
        name: newCategory?.name,
      });
      setNewCategory(undefined);
      refetchCategories();
    } catch {
      enqueueSnackbar('Failed to save category', { variant: 'error' });
    }
  }, [collection, newCategory, analytics, refetchCategories]);

  const handleDeleteCollection = useCallback(async () => {
    try {
      analytics.event(AnalyticsEvent.WM_IA_QL_DELETE_COLLECTION);

      await deleteCollection(collection.id);
      refetchCollections();
    } catch {
      enqueueSnackbar('Failed to delete collection', { variant: 'error' });
      setShowDeleteConfirmationDialog(false);
    }
  }, [collection, refetchCollections, analytics]);

  const handleConfirmPublic = useCallback(async () => {
    try {
      const visibility = collection.visibility === 'public' ? 'private' : 'public';

      analytics.event(
        AnalyticsEvent.WM_IA_QL_VISIBILITY_COLLECTION,
        { value: `Visibility=${visibility}` },
        { collectionVisibility: visibility }
      );

      await updateCollection({ id: collection.id, visibility });
      refetchCollections();
    } catch {
      enqueueSnackbar('Failed to make collection public', { variant: 'error' });
    } finally {
      setShowPublicConfirmationDialog(false);
    }
  }, [collection, refetchCollections, analytics]);

  const toggleVisibility = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      // If the collection is private, show confirmation dialog
      if (collection.visibility === 'private') {
        setShowPublicConfirmationDialog(true);
        return;
      }

      try {
        analytics.event(
          AnalyticsEvent.WM_IA_QL_VISIBILITY_COLLECTION,
          { value: `Visibility:private` },
          { collectionVisibility: 'private' }
        );
        // If the collection is public, toggle it directly
        await updateCollection({ id: collection.id, visibility: 'private' });
        refetchCollections();
      } catch {
        enqueueSnackbar('Failed to make collection private', { variant: 'error' });
      }
    },
    [collection, refetchCollections, analytics]
  );

  const handleShare = useCallback(
    async (selectedUsers: string[]) => {
      try {
        analytics.event(
          AnalyticsEvent.WM_IA_QL_SHARE_COLLECTION,
          { value: `Share:${selectedUsers}` },
          {
            collectionShared: selectedUsers,
          }
        );

        await updateCollection({ id: collection.id, share: selectedUsers });
        refetchCollections();
      } catch {
        enqueueSnackbar('Failed to share collection', { variant: 'error' });
      }
    },
    [collection, refetchCollections, analytics]
  );

  const saveCollection = useCallback(
    async (name: string, description?: string, tags?: string[]) => {
      try {
        analytics.event(
          AnalyticsEvent.WM_IA_QL_EDIT_COLLECTION,
          {
            value: `Collection=${collection.id},Tags=${tags?.join(',')}`,
          },
          {
            collectionId: collection.id,
            collectionTags: tags,
          }
        );

        await updateCollection({
          id: collection.id,
          name,
          description,
          tags,
        });
        setEditingCollection(false);
        refetchCollections();
      } catch {
        enqueueSnackbar('Failed to save collection', { variant: 'error' });
      }
    },
    [collection.id, refetchCollections, analytics]
  );

  const saveCollectionPrompt = useCallback(
    async (newPrompt: string) => {
      try {
        analytics.event(AnalyticsEvent.WM_IA_QL_COLLECTION_CUSTOM_PROMPT);

        await updateCollection({
          id: collection.id,
          customPrompt: newPrompt,
        });
        setShowEditPromptDialog(false);
        refetchCollections();
      } catch {
        enqueueSnackbar('Failed to save collection prompt', { variant: 'error' });
      }
    },
    [collection.id, refetchCollections, analytics]
  );

  const saveCategoriesOrder = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      try {
        e.stopPropagation();
        await reorderCategories(orderedCategories);
        refetchCategories();
      } catch {
        enqueueSnackbar('Failed to reorder categories.', { variant: 'error' });
      } finally {
        setReorderingCategories(false);
      }
    },
    [orderedCategories, refetchCategories]
  );

  const resetCategoriesOrder = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      setOrderedCategories(categories ?? []);
      setReorderingCategories(false);
    },
    [categories, setOrderedCategories]
  );

  // If in the public tab, we dont allow editing the collection
  useEffect(() => {
    if (tab === QuestionLibraryTabs.PUBLIC_COLLECTIONS) {
      setEditingCollection(false);
    }
  }, [tab]);

  const verified = useMemo(() => collection.tags.includes(CollectionTags.VERIFIED), [collection]);

  const questionCount = useMemo(() => {
    return categories
      ? categories.reduce((acc, c) => acc + (c.questionCount || 0), 0)
      : collection.questionCount;
  }, [categories, collection]);

  return (
    <StyledAccordion
      ref={ref}
      expanded={expanded}
      onChange={handleAccordionChange}
      sx={{
        ...highlightSx,
        ...(highlight && {
          '&.MuiAccordion-root:last-child': {
            borderBottom: '2px solid #F900D3',
          },
        }),
      }}
    >
      <StyledAccordionSummary expandIcon={<ChevronRightSharp />}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            ...(editingCollection && { flex: 1 }),
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 0.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexGrow: 1,
                gap: 1,
              }}
            >
              {editingCollection ? (
                <CollectionForm
                  collection={collection}
                  onSave={saveCollection}
                  onCancel={() => setEditingCollection(false)}
                />
              ) : (
                <>
                  {verified && (
                    <Tooltip title="Verified Collection" arrow>
                      <Verified color="primary" />
                    </Tooltip>
                  )}
                  <Typography variant="subtitle2">{collection.name}</Typography>
                  <TagSummaryChips tags={collection.tags} omitVerified />
                  {tab !== QuestionLibraryTabs.MY_COLLECTIONS && (
                    <Typography variant="caption">
                      by{' '}
                      {collection.createdBy === currentUser?.id ? 'You' : collection.createdByName}
                    </Typography>
                  )}
                </>
              )}
              {!editingCollection && !!collection.description && (
                <CustomTooltip
                  title={<span onClick={(e) => e.stopPropagation()}>{collection.description}</span>}
                >
                  <InfoOutlined sx={{ width: '20px' }} />
                </CustomTooltip>
              )}
            </Box>
            {!editingCollection && (
              <Typography sx={{ fontStyle: 'italic' }}>
                {questionCount
                  ? `${questionCount} question${questionCount > 1 ? 's' : ''}`
                  : 'No questions'}
              </Typography>
            )}
          </Box>
          {!editingCollection &&
            !reorderingCategories &&
            tab !== QuestionLibraryTabs.PUBLIC_COLLECTIONS && (
              <Tooltip title="New Category" arrow>
                <StyledIconButton onClick={onNewCategory}>
                  <Add />
                </StyledIconButton>
              </Tooltip>
            )}
        </Box>
        {!editingCollection &&
          !reorderingCategories &&
          tab !== QuestionLibraryTabs.PUBLIC_COLLECTIONS && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {collection.createdBy === currentUser?.id && (
                <Tooltip
                  title={`${collection.visibility === 'public' ? 'Public' : 'Private'} Collection`}
                  arrow
                >
                  <StyledIconButton onClick={toggleVisibility}>
                    {collection.visibility === 'public' ? (
                      <VisibilityRounded />
                    ) : (
                      <VisibilityOffRounded />
                    )}
                  </StyledIconButton>
                </Tooltip>
              )}
              <ShareButton
                sharedWith={collection.sharedWith}
                onSave={handleShare}
                ignoreIds={[collection.createdBy]}
              />
              {(isAdmin || features.ql_custom_prompt) && (
                <Tooltip
                  title={
                    collection.customPrompt
                      ? 'This collection uses a custom prompt, which will be applied to all questions that do not have their own custom prompt'
                      : 'Define a custom prompt for this collection'
                  }
                  arrow
                >
                  <StyledIconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowEditPromptDialog(true);
                    }}
                  >
                    <Badge
                      badgeContent={collection.customPrompt ? '!' : undefined}
                      color="primary"
                      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                      overlap="circular"
                    >
                      <AutoAwesomeRounded />
                    </Badge>
                  </StyledIconButton>
                </Tooltip>
              )}
              <CollectionContextMenu
                collection={collection}
                setExpanded={setExpanded}
                setReordering={setReorderingCategories}
                setEditingCollection={setEditingCollection}
                setShowDeleteConfirmationDialog={setShowDeleteConfirmationDialog}
              />
            </Box>
          )}
        {reorderingCategories && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Click to save changes" arrow>
              <StyledIconButton onClick={saveCategoriesOrder}>
                <SaveOutlined />
              </StyledIconButton>
            </Tooltip>
            <Tooltip title="Cancel" arrow>
              <StyledIconButton onClick={resetCategoriesOrder}>
                <ClearRounded />
              </StyledIconButton>
            </Tooltip>
          </Box>
        )}
      </StyledAccordionSummary>
      <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', padding: 2, gap: 2 }}>
        {newCategory && (
          <Box sx={{ display: 'flex', gap: 1, paddingLeft: 2 }}>
            <TextField
              label="New Category"
              variant="outlined"
              size="small"
              value={newCategory.name}
              onChange={(e) => setNewCategory((prev) => ({ ...prev, name: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveCategory();
                }
              }}
              sx={{ width: '400px' }}
              fullWidth
              autoFocus
            />
            <Tooltip title="Save" arrow>
              <StyledIconButton disabled={!newCategory.name} onClick={handleSaveCategory}>
                <SaveOutlined />
              </StyledIconButton>
            </Tooltip>
            <Tooltip title="Cancel" arrow>
              <StyledIconButton onClick={() => setNewCategory(undefined)}>
                <ClearRounded />
              </StyledIconButton>
            </Tooltip>
          </Box>
        )}
        <Box>
          {loadingCategories ? (
            Array.from({ length: 3 }).map((_, index) => (
              <CategoryAccordionSkeleton
                key={index}
                hasActions={tab !== QuestionLibraryTabs.PUBLIC_COLLECTIONS}
              />
            ))
          ) : categories && categories.length > 0 ? (
            reorderingCategories ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
              >
                <SortableContext
                  items={orderedCategories.map((q) => q.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {orderedCategories.map((category) => (
                    <CategoryAccordion
                      key={category.id}
                      editable={tab !== QuestionLibraryTabs.PUBLIC_COLLECTIONS}
                      category={category}
                      reorderingCategories={reorderingCategories}
                      refetchCategories={refetchCategories}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              categories.map((category) => (
                <CategoryAccordion
                  key={category.id}
                  editable={tab !== QuestionLibraryTabs.PUBLIC_COLLECTIONS}
                  category={category}
                  reorderingCategories={reorderingCategories}
                  defaultExpanded={categories.length === 1}
                  refetchCategories={refetchCategories}
                />
              ))
            )
          ) : (
            <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
              No categories found. Create a new category to get started.
            </Typography>
          )}
        </Box>
        {showPublicConfirmationDialog && (
          <ConfirmationDialog
            title="Public Collection?"
            message="By making this collection public, you are allowing all users to view and use these
        questions."
            close={() => setShowPublicConfirmationDialog(false)}
            confirm={handleConfirmPublic}
          />
        )}
        {showDeleteConfirmationDialog && (
          <ConfirmationDialog
            title="Delete Collection?"
            message="Are you sure you want to delete this collection? This action cannot be undone."
            close={() => setShowDeleteConfirmationDialog(false)}
            confirm={handleDeleteCollection}
          />
        )}
        {showEditPromptDialog && (
          <EditableDialog
            title="Edit Collection Prompt"
            message="You're about to replace the default prompt. Make sure your custom prompt is well-formatted so the system can correctly append the user's question at the end of the prompt"
            initialValue={collection.customPrompt || QuestionLibraryDefaultPrompt}
            close={() => setShowEditPromptDialog(false)}
            save={saveCollectionPrompt}
          />
        )}
      </AccordionDetails>
    </StyledAccordion>
  );
};

export default CollectionAccordion;
